export interface SessionState {
  sessionId: string | null;
  userName: string | null;
  lastActivity: Date;
  isProfileUpdated?: boolean;
}

class SessionManager {
  private readonly STORAGE_KEY = 'chat_session_state';
  private readonly SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

  public saveSessionState(sessionId: string | null, userName: string | null): void {
    try {
      if (!sessionId || !userName) {
        console.warn('Attempted to save session with null values');
        return;
      }

      const sessionState: SessionState = {
        sessionId,
        userName,
        lastActivity: new Date(),
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionState));
    } catch (error) {
      console.warn('Failed to save session state to localStorage:', error);
    }
  }

  public getStoredSessionState(): SessionState | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const sessionState: SessionState = JSON.parse(stored);

      // Check if session is expired
      const now = new Date();
      const lastActivity = new Date(sessionState.lastActivity);
      const timeDiff = now.getTime() - lastActivity.getTime();

      if (timeDiff > this.SESSION_TIMEOUT) {
        this.clearStoredSessionState();
        return null;
      }

      return sessionState;
    } catch (error) {
      console.warn('Failed to retrieve session state from localStorage:', error);
      this.clearStoredSessionState();
      return null;
    }
  }

  public clearStoredSessionState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem('isProfileUpdated');
    } catch (error) {
      console.warn('Failed to clear session state from localStorage:', error);
    }
  }

  public getSessionIdFromUrl(): string | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('session');
    } catch (error) {
      console.warn('Failed to get session ID from URL:', error);
      return null;
    }
  }

  public handleBrowserNavigation(callback: (sessionId: string | null) => void): () => void {
    const handlePopState = (event: PopStateEvent) => {
      const sessionId = event.state?.sessionId || this.getSessionIdFromUrl();
      callback(sessionId);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }

  public updateSessionState(sessionId: string | null, userName: string | null, isProfileUpdated?: boolean): void {
    try {
      if (!sessionId || !userName) {
        console.warn('Attempted to update session with null values');
        return;
      }

      const sessionState: SessionState = {
        sessionId,
        userName,
        lastActivity: new Date(),
        isProfileUpdated,
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionState));
    } catch (error) {
      console.warn('Failed to update session state to localStorage:', error);
    }
  }
}

export const sessionManager = new SessionManager();
export default sessionManager;